/* eslint-disable react-hooks/rules-of-hooks */
import { useQuery } from "@apollo/client";

import { LIST_OWN_OEM_SUPPORT_TICKETS_QUERY } from "~/api";
import { queryTracker } from "~/atoms";
import { ITEMS_PER_PAGE } from "~/constants";
import { transformSupportTickets } from "~/transformers/_supportTicketTransformer";
import { generateListOwnOemSupportTicketsVariables } from "~/utils/filter/_supportTicketQueryCond";

interface ListOwnOemSupportTicketsArgs {
  activeFilters?: Record<string, unknown>;
  limit?: number;
  skip?: number;
  searchQuery?: string;
  where?: Record<string, unknown>;
  skipCondition?: boolean;
  sort?: string[];
  notifyOnNetworkStatusChange?: boolean;
}

/**
 * Support-ticket list query hook, backed by the dedicated
 * `listOwnOemSupportTickets` endpoint. Results are normalized via
 * `transformSupportTickets` so the shared list/table views keep rendering the
 * Work-Order-style fields they expect.
 */
export const useListOwnOemSupportTickets = ({
  activeFilters,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  where,
  skipCondition,
  sort = [],
  notifyOnNetworkStatusChange = true,
}: ListOwnOemSupportTicketsArgs = {}) => {
  const variables = generateListOwnOemSupportTicketsVariables({
    activeFilters,
    limit,
    skip,
    searchQuery,
    where,
    sort,
  });
  queryTracker.add([{ key: "LIST_OWN_OEM_SUPPORT_TICKETS_QUERY", variables }]);

  const { data, previousData, loading, refetch, fetchMore } = useQuery(
    LIST_OWN_OEM_SUPPORT_TICKETS_QUERY,
    {
      variables,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange,
      skip: skipCondition,
    },
  );

  // Keep prior rows while sort/filter variables change — avoids full-table
  // skeleton flash (new variables miss cache until the network responds).
  const listOwnOemSupportTickets =
    data?.listOwnOemSupportTickets ??
    previousData?.listOwnOemSupportTickets ??
    {};

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    supportTickets = [],
  } = listOwnOemSupportTickets;

  const handleFetchMore = ({
    limit: moreLimit,
    skip: moreSkip,
  }: {
    limit: number;
    skip: number;
  }) => {
    const moreVariables = generateListOwnOemSupportTicketsVariables({
      activeFilters,
      limit: moreLimit,
      skip: moreSkip,
      searchQuery,
      where,
      sort,
    });
    fetchMore({
      variables: moreVariables,
      updateQuery: (previousResult: any, { fetchMoreResult }: any) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedTickets = [
          ...previousResult.listOwnOemSupportTickets.supportTickets,
          ...fetchMoreResult.listOwnOemSupportTickets.supportTickets,
        ];

        return {
          ...previousResult,
          listOwnOemSupportTickets: {
            ...previousResult.listOwnOemSupportTickets,
            ...fetchMoreResult.listOwnOemSupportTickets,
            supportTickets: mergedTickets,
          },
        };
      },
    });
  };

  return {
    tickets: transformSupportTickets(supportTickets),
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
  };
};
